import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArticleId, CmsTeaserBlock } from "@/gen/realtime";
import { useImageUpload } from "@/hooks/use-image-upload";

import { teaserImageUrlWithRatio } from "../layoutUtils";
import { FormControl, FormLabel } from "./shared";

const teaserKinds = ["article", "tournament", "other"] as const;
const presentations = [
    { key: "default", title: "4/6 (Default)" },
    { key: "defaultWide", title: "16/9 (Videos)" },
    { key: "posterMd", title: "1/1 (Poster)" },
    { key: "original", title: "Original" },
] as const;

export const TeaserForm = ({
    block,
    setBlock,
}: {
    block: CmsTeaserBlock;
    setBlock: (mutate: (block: CmsTeaserBlock) => void) => void;
}) => {
    const { startSelectFile, inputProps, inputId, isUploading } = useImageUpload({
        onUploadFinished: imageId => {
            setBlock(block => {
                block.imageId = imageId;
            });
        },
    });

    return (
        <div className="flex-grow space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Teaser Type</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex flex-row gap-4">
                        {teaserKinds.map(kind => (
                            <Button
                                key={kind}
                                onClick={() => {
                                    setBlock(block => {
                                        block.teaserKind = { t: kind, c: null };
                                    });
                                }}
                                variant={block.teaserKind.t === kind ? "default" : "outline"}
                            >
                                {kind}
                            </Button>
                        ))}
                    </div>
                    <TeaserKindForm block={block} setBlock={setBlock} />
                </CardContent>
            </Card>
            <div className="space-y-2">
                <div className="text-md font-bold">Size</div>
                <div className="flex gap-2">
                    {presentations.map(size => (
                        <Button
                            key={size.key}
                            onClick={() => {
                                setBlock(block => {
                                    block.presentation = { t: size.key };
                                });
                            }}
                            variant={block.presentation.t === size.key ? "default" : "outline"}
                        >
                            {size.title}
                        </Button>
                    ))}
                </div>
            </div>
            <FormItem>
                <FormLabel className="text-md font-bold">Title</FormLabel>
                <FormControl>
                    <Input
                        placeholder="Teaser title"
                        value={block.title}
                        onChange={e =>
                            setBlock(block => {
                                block.title = e.target.value;
                            })
                        }
                    />
                </FormControl>
            </FormItem>
            <FormItem>
                <FormLabel className="text-md font-bold">Lead text</FormLabel>
                <FormControl>
                    <Input
                        placeholder="Lead text"
                        value={block.leadText ?? ""}
                        onChange={e =>
                            setBlock(block => {
                                block.leadText = e.target.value;
                            })
                        }
                    />
                </FormControl>
            </FormItem>
            <FormItem>
                <FormLabel className="text-md font-bold">Label</FormLabel>
                <FormControl>
                    <Input
                        placeholder="Label"
                        value={block.label ?? ""}
                        onChange={e =>
                            setBlock(block => {
                                block.label = e.target.value;
                            })
                        }
                    />
                </FormControl>
            </FormItem>
            <div className="flex flex-row gap-4">
                {(["blue", "green"] as const).map(labelColor => (
                    <Button
                        key={labelColor}
                        onClick={() => {
                            setBlock(block => {
                                block.labelColor = { t: labelColor };
                            });
                        }}
                        variant={block.labelColor.t === labelColor ? "secondary" : "ghost"}
                    >
                        {labelColor}
                    </Button>
                ))}
            </div>
            <div className="flex flex-row gap-4">
                {(["lg", "md"] as const).map(labelStyle => (
                    <Button
                        key={labelStyle}
                        onClick={() => {
                            setBlock(block => {
                                block.labelStyle = { t: labelStyle };
                            });
                        }}
                        variant={block.labelStyle.t === labelStyle ? "secondary" : "ghost"}
                    >
                        {labelStyle}
                    </Button>
                ))}
            </div>
            <div className="space-y-2">
                <input {...inputProps} />
                <Label className="text-md font-bold" htmlFor={inputId}>
                    Image
                </Label>
                {block.imageId ? (
                    <div className="">
                        <img
                            key={block.imageId}
                            src={teaserImageUrlWithRatio(block.presentation, block.imageId)}
                        />
                        <Button
                            variant="secondary"
                            onClick={() => startSelectFile()}
                            disabled={isUploading}
                        >
                            {isUploading ? "Uploading..." : "Change"}
                        </Button>
                    </div>
                ) : (
                    <div>
                        <Button variant="secondary" onClick={() => startSelectFile()}>
                            Upload image
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

const TeaserKindForm = ({
    block,
    setBlock,
}: {
    block: CmsTeaserBlock;
    setBlock: (mutate: (block: CmsTeaserBlock) => void) => void;
}) => {
    switch (block.teaserKind.t) {
        case "article":
            return (
                <FormItem>
                    <FormLabel className="text-md font-bold">Article ID</FormLabel>
                    <FormControl>
                        <Input
                            placeholder="id"
                            value={block.teaserKind.c ?? ""}
                            onChange={e =>
                                setBlock(block => {
                                    block.teaserKind = {
                                        t: "article",
                                        c: e.target.value as ArticleId,
                                    };
                                })
                            }
                        />
                    </FormControl>
                </FormItem>
            );
        case "tournament":
            return (
                <FormItem>
                    <FormLabel className="text-md font-bold">Tournament ID</FormLabel>
                    <FormControl>
                        <Input
                            placeholder="id"
                            value={block.teaserKind.c ?? ""}
                            onChange={e =>
                                setBlock(block => {
                                    block.teaserKind = {
                                        t: "tournament",
                                        c: e.target.value,
                                    };
                                })
                            }
                        />
                    </FormControl>
                </FormItem>
            );
        case "other":
            return (
                <FormItem>
                    <FormLabel className="text-md font-bold">URL (internal)</FormLabel>
                    <FormControl>
                        <Input
                            placeholder="/player/000-000"
                            value={block.teaserKind.c ?? ""}
                            onChange={e =>
                                setBlock(block => {
                                    block.teaserKind = {
                                        t: "other",
                                        c: e.target.value,
                                    };
                                })
                            }
                        />
                    </FormControl>
                </FormItem>
            );
        default:
            return null;
    }
};
